import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconHeart, IconHeartFilled } from '@tabler/icons-react';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { isPropertyAvailableForPurchase } from 'components/properties/helpers/property';

import type { PropertyDetailsType } from '../types';
import { PricingTraditional } from './Pricing';

interface PropertySummaryProps {
  property: PropertyDetailsType;
}

export const PropertySummary = ({ property }: PropertySummaryProps) => {
  const formattedStreet = formatStreetAddress(
    property.street,
    property.unitType,
    property.unitNumber,
  );
  const availableForPurchase = isPropertyAvailableForPurchase(property);
  const locality = [property.city, property.state].filter(Boolean).join(', ');

  return (
    <Col>
      <Row className="items-start justify-between pb-6">
        <Text as="h1" className="text-subheader-lg">
          <Text as="span" className="text-subheader-sm md:text-subheader-lg">
            {formattedStreet}
          </Text>
          <br />
          <Text
            as="span"
            className="text-subheader-sm text-od-content-tertiary md:text-subheader-lg"
          >
            {[locality, property.zip].filter(Boolean).join(' ')}
          </Text>
        </Text>
        <Box className="rounded-2xl border border-od-border-primary bg-od-bg-primary p-3">
          <Button
            variant="secondary"
            aria-label={property.isLiked ? 'unfavorite' : 'favorite'}
            analyticsName="cosmos-marketplace-pdp-favorite"
            onClick={property.onClickReaction}
            className="size-9 rounded-full border-od-border-tertiary p-0"
          >
            {property.isLiked ? (
              <IconHeartFilled size={24} className="text-od-content-error" />
            ) : (
              <IconHeart size={24} />
            )}
          </Button>
        </Box>
      </Row>
      <Col as="section">
        <PricingTraditional price={getDisplayPrice(property, 'listed')} />
        <Row>
          <Text as="span" className="text-body-md text-od-content-tertiary">
            {property.bedrooms} bds
          </Text>
          <Text as="span" className="px-2 text-body-md text-od-content-tertiary">
            ·
          </Text>
          <Text as="span" className="text-body-md text-od-content-tertiary">
            {property.bathrooms} ba
          </Text>
          <Text as="span" className="px-2 text-body-md text-od-content-tertiary">
            ·
          </Text>
          <Text as="span" className="text-body-md text-od-content-tertiary">
            {property.sqFtTotalLiving} sqft
          </Text>
          {availableForPurchase && property?.estimatedMonthlyPayment && (
            <>
              <Text as="span" className="px-2 text-body-md text-od-content-tertiary">
                ·
              </Text>
              <Text className="text-body-md text-od-content-tertiary">
                {`${formatCurrency(property?.estimatedMonthlyPayment)}/month est.`}
              </Text>
            </>
          )}
        </Row>
        {!!property?.schoolDistricts?.length && (
          <Row>
            <Text as="span" className="pt-1 text-body-md text-od-content-tertiary">
              {property.schoolDistricts.join(' & ')}
            </Text>
          </Row>
        )}
      </Col>
    </Col>
  );
};
